// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../../common/api';
import config from '../../../../share/config';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import printModal from '../../../vue/DiagPrintModal';
import resource from '../../../../share/util/resourceMap';
import util from '../../../../share/util/utils';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';

export default {
  name: "allDtcPage",
  props: {
    // デバッグ環境のみの動作確認
    testModeVisible: { type: Boolean, required: false }
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      measuredDataItems: [],
      labelSwitch: false,
      modalPrintForm: false,
      printUserInfo: {}
    };
  },

  watch: {
    language() {
      this.updateColumns();
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
    'print-modal': printModal,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * ALL DTC計測データ一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     */
    async createAllDtcMeasuresList(projectId, measuresId) {
      try {
        this.processing = true;
        const reproEndPoint = '/diagDtc/' + projectId + '/' + measuresId;
        const params = { region_info: this.cachedProjectDataSource };
        const resRepro = await api.getParamCall(config.DIAG, reproEndPoint, params, this.operationScreen);

        handler.validate(handler.validateTypes.all, resRepro, this, null, () => {
          this.recollectedData = resRepro.data;
          this.updateColumns();
          this.updateDTCTable();
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定 */
    async updateColumns() {
      const igOnCntVar = util.mkHeaderValue(this.recollectedData.cgw_igon_time);
      const igOnCnt = this.$t('message.header_vehicle_dtc_table_igon_counts');
      const igOnCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igOnCntVar;
      const igOnTimeVar = util.mkHeaderValue(this.recollectedData.cgw_igon_mill_sec);
      const igOnTime = this.$t('message.header_vehicle_dtc_table_igon_times');
      const igOnTimeValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igOnTimeVar;
      const counterType = this.$t('message.header_vehicle_dtc_table_type');
      const current = this.$t('message.a_status_vehicle_dtc_status_current');
      this.measuredDataColumns = [
        { name: 'name', label: 'message.header_vehicle_table_system_name', useResource: true },
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', useResource: true },
        { name: 'dtc', label: 'message.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'message.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'igonCount', label: util.setColums(igOnCnt, igOnCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },  // ハイフンは翻訳リソースを使わない
        { name: 'igonTime', label: util.setColums(igOnTime, igOnTimeValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },  // ハイフンは翻訳リソースを使わない
        { name: 'counterType', label: util.setColums(counterType, current, "{0}"), useHtmlBind: true },
        { name: 'ffd', label: 'message.header_vehicle_dtc_table_ffd', useButton: true, useResource: true, originalSortFunc: diagnoseResource.sortFuncFfd },
      ];
    },

    // 連鎖DTC表示/非表示
    updateDTCTable() {
      let dtcListItems = [];
      // DTC情報をシステムIDでグループ化
      const groupBySystem = Object.keys(util.groupBy(this.recollectedData.data, 'system'));
      for (const diagSystem of groupBySystem) {
        // DTC情報をシステムIDでフィルター且つ、連鎖DTCでフィルター
        const filteredDtcSystems = this.recollectedData.data
          .filter(dtc => dtc.system === diagSystem)
          .filter(dtc => this.labelSwitch || !dtc.is_chain_dtc);

        // フィルターしたDTC情報から、テーブルを作成する
        const targetDtcList = resource.createDtcTable(filteredDtcSystems, diagSystem, this, this.labelSwitch);
        dtcListItems = dtcListItems.concat(targetDtcList);
      }
      // ALL DTCテーブル作成
      this.measuredDataItems = dtcListItems;
      this.measuredDataItems.map(e => {
        if (e.is_chain_dtc) {
          e.bgStyle = `${'dtc-cursor-default'} ${'is-chain-dtc-back-color'}`.trim();
        } else {
          e.bgStyle = 'dtc-cursor-default';
        }
      });
    },

    /** 帳票印刷 */
    openPrintModal() {
      // ユーザー情報登録
      this.printUserInfo = {
        name: this.cachedDetails.information.registered_by,
        language: this.language,
        region: this.userInfo.region
      };
      this.modalPrintForm = true;
    },

    /** 帳票印刷を実行 */
    startPrint() {
      this.$refs.print_modal.print();
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /** 連鎖DTC表示/非表示切り替え */
    switchDtcChain() {
      this.labelSwitch = !this.labelSwitch;
      this.updateDTCTable();
    },

    /**
     * ファイルエクスポートボタン押下.
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している
     */
    csvExport() {
      this.$emit('csvExport', this.measuredDataColumns, this.measuredDataItems, this.recollectedData);
    },

    /**
     * FFDボタン押下.
     * DataRecollection.jsのFFDModalを呼び出している
     * @param {Array} row 一覧の選択した行
     */
    async rowButtonClickEvent(row) {
      await this.openFFDModal(row);
    },

    /**
     * FFDボタン押下.
     * @param {Object} arg 選択された行
     */
    openFFDModal(arg) {
      this.$emit('openFFDModal', arg);
    },

    /** DataRecollection.jsの関数を呼び出す */
    async debugExport() {
      await this.dtcTestDataExport();
    },

    /** DTCテストデータエクスポート  */
    dtcTestDataExport() {
      this.$emit('dtcTestDataExport', this.recollectedData);
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedDetails: state => state.cache.projectDetails,
      userInfo: state => state.userProperty.userInfo,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};